import type { RootState } from "../state";
import { getDefaultRoomState } from "../state";
import { emitter } from "@/utils/emitter/mitt";
import {
  isAppleLike,
  showIOSSwitchSuccessIfNeeded,
  setIOSSwitchLoading
} from "./iosSwitchToast";
import { ElMessage } from "element-plus";
import { listProducts } from "@/api/myProject";

export async function fetchProducts(this: RootState) {
  const res = await listProducts({ pageNum: 1, pageSize: 1000 });
  if (
    res.success &&
    res.result &&
    res.result.page &&
    Array.isArray(res.result.page.records)
  ) {
    this.products = res.result.page.records.filter(p => p.isSucceed !== "-1");
  } else {
    this.products = [];
  }
}

// 初始化相关方法

export function initialize(
  this: RootState & { [key: string]: any },
  initialProductList: any
) {
  // 移除自动权限恢复逻辑，现在只在进入直播间时才需要用户交互

  const roomId = initialProductList?.id;

  // 🆕 处理新建直播间（没有ID）的情况
  if (!roomId) {
    const tempRoomId = "NEW_ROOM";

    // 为新建直播间创建临时状态
    this.currentRoomId = tempRoomId;
    this.roomStates.set(tempRoomId, getDefaultRoomState());

    const roomState = this.roomStates.get(tempRoomId);
    roomState.isInitialized = true;
    roomState.productList = JSON.parse(
      JSON.stringify(initialProductList || {})
    );

    roomState.productList.id = tempRoomId;

    // 初始化高性能队列
    this.initHighPerformanceQueue(tempRoomId);

    // 新建直播间的基本设置
    if (!roomState.productList.name) {
      roomState.productList.name = "新建直播间";
    }
    roomState.productList.status = "draft";

    // 🚨 修复：新建直播间的轮播状态初始化为空
    roomState.carouselInterval = "";
    roomState.insertSegmentList = [];
    // roomState.isCarouselPlaying = false;

    // 🎵 重要：新建直播间也需要加载音色数据（异步执行）
    // this.fetchInitialVoiceData();
    return;
  }

  const previousRoomId = this.currentRoomId;

  // 强化：完全停止上一个房间的所有功能
  if (
    previousRoomId &&
    previousRoomId !== roomId &&
    this.roomStates.has(previousRoomId)
  ) {
    console.log(`🔄 从房间${previousRoomId}切换到房间${roomId}`);
    const previousRoomState = this.roomStates.get(previousRoomId);

    // 完全停止上一个房间的播放器
    if ((previousRoomState as any).howlerPlayer) {
      const prevPlayer = (previousRoomState as any).howlerPlayer;
      console.log(`🎵 完全停止房间${previousRoomId}的播放器`);
      prevPlayer.setMuted(true);
      prevPlayer.setVolume(0);
      prevPlayer.stop();
      prevPlayer.destroy();
      (previousRoomState as any).howlerPlayer = null;
    }

    // 停止播放状态
    previousRoomState.isPlaying = false;

    // 断开上一个房间的WebSocket连接
    try {
      console.log(`🔌 断开房间${previousRoomId}的WebSocket连接`);
      this.leaveRoomWebSocket(previousRoomId);
    } catch (error) {
      console.warn(`🔌 断开房间${previousRoomId}WebSocket时出错:`, error);
    }
  }

  this.currentRoomId = roomId;

  // 🔧 修复：更新所有WebAudio播放器的房间隔离状态
  try {
    if (typeof (this as any).updateAppleWebAudioRoomIsolation === "function") {
      (this as any).updateAppleWebAudioRoomIsolation();
    }
  } catch (e) {
    console.warn("更新WebAudio房间隔离状态失败:", e);
  }

  // 抖音式：所有直播间都在后台播放，不需要特殊处理

  // 简化逻辑：直接更新音频状态，不检查用户交互
  this.updateAudioStateAfterActivation();

  // 全局一次性绑定：Howler 可听事件 → 统一延迟显示“切换成功”并关闭加载
  try {
    if (!(this as any)._audibleListenerBound) {
      (this as any)._audibleListenerBound = true;
      emitter.on("apple:howler-audible", (payload: any) => {
        try {
          // iOS/AppleLike 设备使用 WebAudio 分支的起播回调来显示切换成功弹窗；
          // 这里的 Howler audible 在 iOS 场景下可能来自预热/预加载，容易提前触发弹窗，需屏蔽。
          if (isAppleLike()) return;
          const rid = payload && payload.roomId;
          if (!rid) return;
          const st = this.roomStates.get(rid);
          if (!st) return;
          if ((st as any).pendingSwitchSuccessMessage) {
            // 二次保险：若出现进度继承，尝试归零
            try {
              st.currentAudioTime = 0;
              if (st.currentAudioState) st.currentAudioState.currentTime = 0;
              const hp: any = (st as any).howlerPlayer;
              const curHowl: any = hp && (hp as any).currentHowl;
              const node: HTMLAudioElement | undefined =
                curHowl &&
                curHowl._sounds &&
                curHowl._sounds[0] &&
                curHowl._sounds[0]._node;
              if (node && typeof node.currentTime === "number") {
                if (node.currentTime > 0.05) node.currentTime = 0;
              }
            } catch {}

            // 成功提示与关闭 loading 放到“可听”时刻
            ElMessage.success("产品切换成功");
            try {
              const pid = st.switchingProductId || st.selectedProductId;
              if (pid && st.cardLoadingMap) st.cardLoadingMap[pid] = false;
            } catch {}
            delete (st as any).pendingSwitchSuccessMessage;
          }
        } catch {}
      });
    }
  } catch {}

  // 全局绑定：统一处理音频“开始播读”事件，集中控制弹窗与 loading
  try {
    if (!(this as any)._productAudioStartedBound) {
      (this as any)._productAudioStartedBound = true;
      emitter.on("productAudioStarted", (payload: any) => {
        try {
          const rid = payload?.roomId;
          if (!rid) return;
          const st = this.roomStates.get(rid);
          if (!st || !(st as any).pendingSwitchSuccessMessage) return;

          const audioId = payload?.audioId ?? null;
          const lastShown = (st as any).__iosSwitchToastLastAudioId;
          if (audioId && lastShown && lastShown === audioId) return;

          const pidFromEvent = payload?.productId ?? null;
          const fallbackPid =
            pidFromEvent ||
            st.switchingProductId ||
            st.selectedProductId ||
            null;
          try {
            console.log(
              `[productAudioStarted] source=${payload?.source || "unknown"} room=${rid} pid=${fallbackPid || "NA"} audio=${audioId || "NA"} pending=${!!(st as any).pendingSwitchSuccessMessage}`
            );
          } catch {}
          showIOSSwitchSuccessIfNeeded.call(this, rid, fallbackPid, audioId);
          if (fallbackPid) {
            try {
              setIOSSwitchLoading.call(this, fallbackPid, false);
            } catch {}
            try {
              if (st.cardLoadingMap) st.cardLoadingMap[fallbackPid] = false;
            } catch {}
          }
        } catch (err) {
          console.warn("[productAudioStarted] handler error", err);
        }
      });
    }
  } catch {}

  if (!this.roomStates.has(roomId)) {
    this.roomStates.set(roomId, getDefaultRoomState());
    const roomState = this.roomStates.get(roomId);

    roomState.isInitialized = true;
    roomState.productList = JSON.parse(JSON.stringify(initialProductList));

    // 修复：将promotions数组映射为products数组
    if (
      roomState.productList.promotions &&
      Array.isArray(roomState.productList.promotions)
    ) {
      roomState.productList.products = roomState.productList.promotions;
      roomState.selectedProductId = roomState.productList.products[0]?.id || "";
      roomState.productDetail = roomState.productList.products[0] || {};
      roomState.selectedProductIds =
        roomState.productList.products.map(p => p.id) || [];
    } else if (
      roomState.productList.products &&
      Array.isArray(roomState.productList.products)
    ) {
      // 如果已经有products数组，直接使用
      roomState.selectedProductId = roomState.productList.products[0]?.id || "";
      roomState.productDetail = roomState.productList.products[0] || {};
      roomState.selectedProductIds =
        roomState.productList.products.map(p => p.id) || [];
    } else {
      roomState.productList.products = [];
      roomState.selectedProductId = "";
      roomState.productDetail = {};
      roomState.selectedProductIds = [];
    }

    roomState.roomLink = roomState.productList.liveUrl || "";

    // 初始化高性能队列
    this.initHighPerformanceQueue(roomId);

    // 🚨 修复：从直播间列表数据中读取intervalTime并回填
    roomState.carouselInterval = String(
      roomState.productList.intervalTime || ""
    );
    // roomState.isCarouselPlaying = false;

    this.fetchInitialVoiceData();
  } else {
    // 如果房间已存在，更新产品列表信息
    const roomState = this.roomStates.get(roomId);

    // 保存当前用户的选择状态
    const currentSelectedProductId = roomState.selectedProductId;
    const currentSelectedProductIds = roomState.selectedProductIds;

    roomState.productList = JSON.parse(JSON.stringify(initialProductList));

    // 修复：将promotions数组映射为products数组
    if (
      roomState.productList.promotions &&
      Array.isArray(roomState.productList.promotions)
    ) {
      roomState.productList.products = roomState.productList.promotions;
    }
    // 如果已有产品列表，仅更新必要的状态信息（如status）
    if (initialProductList && initialProductList.status) {
      roomState.productList.status = initialProductList.status;
    }
    if (initialProductList && initialProductList.intervalTime !== undefined) {
      roomState.productList.intervalTime = initialProductList.intervalTime;
    }
    if (initialProductList && initialProductList.name) {
      roomState.productList.name = initialProductList.name;
    }
    roomState.liveStyle = {
      strategyType: roomState.productDetail.strategyType || "0",
      contentStrategy: roomState.productDetail.contentStrategy || "",
      weightStrategy: roomState.productDetail.weightStrategy || 0
    };
    console.log(
      `📦 保留已存在的产品列表数据，共${roomState.productList.products.length}个产品`
    );

    // 关键修复：恢复用户的选择状态，避免多用户登录时状态被覆盖
    if (currentSelectedProductId && roomState.productList.products) {
      // 检查当前选择的产品是否仍然存在于新的产品列表中
      const selectedProductExists = roomState.productList.products.find(
        p => p.id === currentSelectedProductId
      );

      if (selectedProductExists) {
        // 如果存在，保留用户的选择
        roomState.selectedProductId = currentSelectedProductId;
        roomState.productDetail = selectedProductExists;
        roomState.selectedProductIds =
          currentSelectedProductIds ||
          roomState.productList.products.map(p => p.id);
      } else {
        // 如果不存在，选择第一个产品
        roomState.selectedProductId =
          roomState.productList.products[0]?.id || "";
        roomState.productDetail = roomState.productList.products[0] || {};
        roomState.selectedProductIds =
          roomState.productList.products.map(p => p.id) || [];
      }
    } else {
      // 如果之前没有选择，选择第一个产品
      roomState.selectedProductId =
        roomState.productList.products?.[0]?.id || "";
      roomState.productDetail = roomState.productList.products?.[0] || {};
      roomState.selectedProductIds =
        roomState.productList.products?.map(p => p.id) || [];
    }

    // 保留用户已输入的roomLink，不要重置
    // 只有当用户没有输入时，才使用产品列表中的默认值
    if (!roomState.roomLink) {
      roomState.roomLink = roomState.productList.liveUrl || "";
    }

    // 重置loading状态，避免进入已存在房间时按钮卡在loading状态
    roomState.startLiveLoading = false;
    roomState.endLiveLoading = false;
    roomState.saveConfigLoading = false;
    roomState.sendMessageLoading = false;
    roomState.simulateCommentLoading = false;
    roomState.bindLinkLoading = false;
    roomState.cardLoadingMap = {};

    // 🚨 修复：从直播间列表数据中读取intervalTime并回填
    roomState.carouselInterval = String(
      roomState.productList.intervalTime || ""
    );
    // roomState.isCarouselPlaying = false;
    const isSelectedProductFailed =
      roomState.productList.products.find(
        p => p.id === roomState.selectedProductId
      )?.isSucceed === "-1";
    roomState.productList.products = roomState.productList.products.filter(
      p => p.isSucceed !== "-1"
    );
    if (isSelectedProductFailed) {
      roomState.selectedProductId = roomState.productList.products[0]?.id || "";
      roomState.productDetail = roomState.productList.products[0] || {};
      roomState.selectedProductIds =
        roomState.productList.products.map(p => p.id) || [];
    }
    this.roomStates.set(roomId, roomState);
  }
  // 音频控制：检查用户是否已交互
  const newRoomState = this.roomStates.get(roomId);
  if ((newRoomState as any).howlerPlayer && this.userHasInteracted) {
    // 修复：只有当前房间才设置有声播放，其他房间强制静音
    const isCurrentRoom = this.currentRoomId === roomId;
    if (isCurrentRoom) {
      (newRoomState as any).howlerPlayer.setMuted(false);
      (newRoomState as any).howlerPlayer.setVolume(1);
      console.log(`🔊 初始化时设置当前房间${roomId}有声播放`);
    } else {
      (newRoomState as any).howlerPlayer.setMuted(true);
      (newRoomState as any).howlerPlayer.setVolume(0);
      console.log(`🔇 初始化时设置非当前房间${roomId}静音播放`);
    }
  }

  // 清除用户交互限制
  // 旧播放器属性已移除，使用 Howler.js 统一管理

  // 检查是否需要恢复播放（刷新后进入直播间的情况）
  if (newRoomState.productList.status === "live" && !newRoomState.isPlaying) {
    // 延迟一下，确保WebSocket连接稳定
    setTimeout(() => {
      const state = this.roomStates.get(roomId);
      if (!state || state.isPlaying) return; // 如果已经在播放了，就不要重复操作

      // 修复：避免与enterRoom函数的playNext调用冲突，导致双重音频播放
      // 添加严格的检查条件，避免在正常播放流程中误触发
      if (
        state.queue.length > 0 &&
        state.currentIndex < state.queue.length &&
        !(state as any).howlerPlayer // 确保没有活跃的音频播放器
      ) {
        // 短暂延迟后再检查，因为enterRoom可能刚刚设置了howlerPlayer
        setTimeout(() => {
          const currentState = this.roomStates.get(roomId);
          if (currentState && !(currentState as any).howlerPlayer) {
            // 确保没有进度计时器运行
            // 检查当前索引的项目是否已经播放完成
            const currentItem = currentState.queue[currentState.currentIndex];
            if (currentItem && currentItem.status !== "played") {
              this.playNext(roomId);
            } else {
              // 如果当前项目已播放完成，让正常的播放流程处理
              if (currentState.currentIndex + 1 < currentState.queue.length) {
                // 直接播放下一个，不修改currentIndex
                this.playNext(roomId);
              } else {
                // 已禁用 - 后端不需要请求消息
                // if (
                //   currentState.sessionId &&
                //   currentState.audioQueueWs?.readyState === WebSocket.OPEN
                // )
              }
            }
          }
        }, 200); // 给enterRoom足够的时间设置howlerPlayer
      } else if (state.queue.length === 0) {
        // 已禁用 - 后端不需要请求消息
        // 如果队列为空，主动请求音频
        // if (
        //   state.sessionId &&
        //   state.audioQueueWs?.readyState === WebSocket.OPEN
        // )
      }
    }, 1000); // 增加延迟时间，确保其他流程先完成
  }

  // 初始化插播列表
  newRoomState.insertSegmentList = [];

  // 保存状态到localStorage
  this.saveStateToStorage();
}

export function initializeExperience(
  this: RootState & { [key: string]: any },
  initialProductList: any
) {
  // 移除自动权限恢复逻辑，现在只在进入直播间时才需要用户交互

  const roomId = initialProductList?.id;

  const previousRoomId = this.currentRoomId;

  // 强化：完全停止上一个房间的所有功能
  if (
    previousRoomId &&
    previousRoomId !== roomId &&
    this.roomStates.has(previousRoomId)
  ) {
    console.log(`🔄 从房间${previousRoomId}切换到房间${roomId}`);
    const previousRoomState = this.roomStates.get(previousRoomId);

    // 完全停止上一个房间的播放器
    if ((previousRoomState as any).howlerPlayer) {
      const prevPlayer = (previousRoomState as any).howlerPlayer;
      console.log(`🎵 完全停止房间${previousRoomId}的播放器`);
      prevPlayer.setMuted(true);
      prevPlayer.setVolume(0);
      prevPlayer.stop();
      prevPlayer.destroy();
      (previousRoomState as any).howlerPlayer = null;
    }

    // 停止播放状态
    previousRoomState.isPlaying = false;

    // 断开上一个房间的WebSocket连接
    try {
      console.log(`🔌 断开房间${previousRoomId}的WebSocket连接`);
      this.leaveRoomWebSocket(previousRoomId);
    } catch (error) {
      console.warn(`🔌 断开房间${previousRoomId}WebSocket时出错:`, error);
    }
  }

  this.currentRoomId = roomId;

  // 🔧 修复：更新所有WebAudio播放器的房间隔离状态
  try {
    if (typeof (this as any).updateAppleWebAudioRoomIsolation === "function") {
      (this as any).updateAppleWebAudioRoomIsolation();
    }
  } catch (e) {
    console.warn("更新WebAudio房间隔离状态失败:", e);
  }

  // 抖音式：所有直播间都在后台播放，不需要特殊处理

  // 简化逻辑：直接更新音频状态，不检查用户交互
  this.updateAudioStateAfterActivation();

  // 全局一次性绑定：Howler 可听事件 → 统一延迟显示“切换成功”并关闭加载
  try {
    if (!(this as any)._audibleListenerBound) {
      (this as any)._audibleListenerBound = true;
      emitter.on("apple:howler-audible", (payload: any) => {
        try {
          // iOS/AppleLike 设备使用 WebAudio 分支的起播回调来显示切换成功弹窗；
          // 这里的 Howler audible 在 iOS 场景下可能来自预热/预加载，容易提前触发弹窗，需屏蔽。
          if (isAppleLike()) return;
          const rid = payload && payload.roomId;
          if (!rid) return;
          const st = this.roomStates.get(rid);
          if (!st) return;
          if ((st as any).pendingSwitchSuccessMessage) {
            // 二次保险：若出现进度继承，尝试归零
            try {
              st.currentAudioTime = 0;
              if (st.currentAudioState) st.currentAudioState.currentTime = 0;
              const hp: any = (st as any).howlerPlayer;
              const curHowl: any = hp && (hp as any).currentHowl;
              const node: HTMLAudioElement | undefined =
                curHowl &&
                curHowl._sounds &&
                curHowl._sounds[0] &&
                curHowl._sounds[0]._node;
              if (node && typeof node.currentTime === "number") {
                if (node.currentTime > 0.05) node.currentTime = 0;
              }
            } catch {}
            try {
              const pid = st.switchingProductId || st.selectedProductId;
              if (pid && st.cardLoadingMap) st.cardLoadingMap[pid] = false;
            } catch {}
            delete (st as any).pendingSwitchSuccessMessage;
          }
        } catch {}
      });
    }
  } catch {}

  // 全局绑定：统一处理音频“开始播读”事件，集中控制弹窗与 loading
  try {
    if (!(this as any)._productAudioStartedBound) {
      (this as any)._productAudioStartedBound = true;
      emitter.on("productAudioStarted", (payload: any) => {
        try {
          const rid = payload?.roomId;
          if (!rid) return;
          const st = this.roomStates.get(rid);
          if (!st || !(st as any).pendingSwitchSuccessMessage) return;

          const audioId = payload?.audioId ?? null;
          const lastShown = (st as any).__iosSwitchToastLastAudioId;
          if (audioId && lastShown && lastShown === audioId) return;

          const pidFromEvent = payload?.productId ?? null;
          const fallbackPid =
            pidFromEvent ||
            st.switchingProductId ||
            st.selectedProductId ||
            null;
          try {
            console.log(
              `[productAudioStarted] source=${payload?.source || "unknown"} room=${rid} pid=${fallbackPid || "NA"} audio=${audioId || "NA"} pending=${!!(st as any).pendingSwitchSuccessMessage}`
            );
          } catch {}
          showIOSSwitchSuccessIfNeeded.call(this, rid, fallbackPid, audioId);
          if (fallbackPid) {
            try {
              setIOSSwitchLoading.call(this, fallbackPid, false);
            } catch {}
            try {
              if (st.cardLoadingMap) st.cardLoadingMap[fallbackPid] = false;
            } catch {}
          }
        } catch (err) {
          console.warn("[productAudioStarted] handler error", err);
        }
      });
    }
  } catch {}

  if (!this.roomStates.has(roomId)) {
    this.roomStates.set(roomId, getDefaultRoomState());
    const roomState = this.roomStates.get(roomId);

    roomState.isInitialized = true;
    roomState.productList = JSON.parse(JSON.stringify(initialProductList));

    // 修复：将promotions数组映射为products数组
    if (
      roomState.productList.promotions &&
      Array.isArray(roomState.productList.promotions)
    ) {
      roomState.productList.products = roomState.productList.promotions;
      roomState.selectedProductId = roomState.productList.products[0]?.id || "";
      roomState.productDetail = roomState.productList.products[0] || {};
      roomState.selectedProductIds =
        roomState.productList.products.map(p => p.id) || [];
    } else if (
      roomState.productList.products &&
      Array.isArray(roomState.productList.products)
    ) {
      // 如果已经有products数组，直接使用
      roomState.selectedProductId = roomState.productList.products[0]?.id || "";
      roomState.productDetail = roomState.productList.products[0] || {};
      roomState.selectedProductIds =
        roomState.productList.products.map(p => p.id) || [];
    } else {
      roomState.productList.products = [];
      roomState.selectedProductId = "";
      roomState.productDetail = {};
      roomState.selectedProductIds = [];
    }

    roomState.roomLink = roomState.productList.liveUrl || "";

    // 初始化高性能队列
    this.initHighPerformanceQueue(roomId);

    // 🚨 修复：从直播间列表数据中读取intervalTime并回填
    roomState.carouselInterval = String(
      roomState.productList.intervalTime || ""
    );
    // roomState.isCarouselPlaying = false;

    // this.fetchInitialVoiceData();
  } else {
    // 如果房间已存在，更新产品列表信息
    const roomState = this.roomStates.get(roomId);

    // 保存当前用户的选择状态
    const currentSelectedProductId = roomState.selectedProductId;
    const currentSelectedProductIds = roomState.selectedProductIds;

    roomState.productList = JSON.parse(JSON.stringify(initialProductList));

    // 修复：将promotions数组映射为products数组
    if (
      roomState.productList.promotions &&
      Array.isArray(roomState.productList.promotions)
    ) {
      roomState.productList.products = roomState.productList.promotions;
    }
    // 如果已有产品列表，仅更新必要的状态信息（如status）
    if (initialProductList && initialProductList.status) {
      roomState.productList.status = initialProductList.status;
    }
    if (initialProductList && initialProductList.intervalTime !== undefined) {
      roomState.productList.intervalTime = initialProductList.intervalTime;
    }
    if (initialProductList && initialProductList.name) {
      roomState.productList.name = initialProductList.name;
    }
    roomState.liveStyle = {
      strategyType: roomState.productDetail.strategyType || "0",
      contentStrategy: roomState.productDetail.contentStrategy || "",
      weightStrategy: roomState.productDetail.weightStrategy || 0
    };
    console.log(
      `📦 保留已存在的产品列表数据，共${roomState.productList.products.length}个产品`
    );

    // 关键修复：恢复用户的选择状态，避免多用户登录时状态被覆盖
    if (currentSelectedProductId && roomState.productList.products) {
      // 检查当前选择的产品是否仍然存在于新的产品列表中
      const selectedProductExists = roomState.productList.products.find(
        p => p.id === currentSelectedProductId
      );

      if (selectedProductExists) {
        // 如果存在，保留用户的选择
        roomState.selectedProductId = currentSelectedProductId;
        roomState.productDetail = selectedProductExists;
        roomState.selectedProductIds =
          currentSelectedProductIds ||
          roomState.productList.products.map(p => p.id);
      } else {
        // 如果不存在，选择第一个产品
        roomState.selectedProductId =
          roomState.productList.products[0]?.id || "";
        roomState.productDetail = roomState.productList.products[0] || {};
        roomState.selectedProductIds =
          roomState.productList.products.map(p => p.id) || [];
      }
    } else {
      // 如果之前没有选择，选择第一个产品
      roomState.selectedProductId =
        roomState.productList.products?.[0]?.id || "";
      roomState.productDetail = roomState.productList.products?.[0] || {};
      roomState.selectedProductIds =
        roomState.productList.products?.map(p => p.id) || [];
    }

    // 保留用户已输入的roomLink，不要重置
    // 只有当用户没有输入时，才使用产品列表中的默认值
    if (!roomState.roomLink) {
      roomState.roomLink = roomState.productList.liveUrl || "";
    }

    // 重置loading状态，避免进入已存在房间时按钮卡在loading状态
    roomState.startLiveLoading = false;
    roomState.endLiveLoading = false;
    roomState.saveConfigLoading = false;
    roomState.sendMessageLoading = false;
    roomState.simulateCommentLoading = false;
    roomState.bindLinkLoading = false;
    roomState.cardLoadingMap = {};

    // 🚨 修复：从直播间列表数据中读取intervalTime并回填
    roomState.carouselInterval = String(
      roomState.productList.intervalTime || ""
    );
    // roomState.isCarouselPlaying = false;
    const isSelectedProductFailed =
      roomState.productList.products.find(
        p => p.id === roomState.selectedProductId
      )?.isSucceed === "-1";
    roomState.productList.products = roomState.productList.products.filter(
      p => p.isSucceed !== "-1"
    );
    if (isSelectedProductFailed) {
      roomState.selectedProductId = roomState.productList.products[0]?.id || "";
      roomState.productDetail = roomState.productList.products[0] || {};
      roomState.selectedProductIds =
        roomState.productList.products.map(p => p.id) || [];
    }
    this.roomStates.set(roomId, roomState);
  }
  // 音频控制：检查用户是否已交互
  const newRoomState = this.roomStates.get(roomId);
  if ((newRoomState as any).howlerPlayer && this.userHasInteracted) {
    // 修复：只有当前房间才设置有声播放，其他房间强制静音
    const isCurrentRoom = this.currentRoomId === roomId;
    if (isCurrentRoom) {
      (newRoomState as any).howlerPlayer.setMuted(false);
      (newRoomState as any).howlerPlayer.setVolume(1);
      console.log(`🔊 初始化时设置当前房间${roomId}有声播放`);
    } else {
      (newRoomState as any).howlerPlayer.setMuted(true);
      (newRoomState as any).howlerPlayer.setVolume(0);
      console.log(`🔇 初始化时设置非当前房间${roomId}静音播放`);
    }
  }

  // 清除用户交互限制
  // 旧播放器属性已移除，使用 Howler.js 统一管理

  // 检查是否需要恢复播放（刷新后进入直播间的情况）
  if (newRoomState.productList.status === "live" && !newRoomState.isPlaying) {
    // 延迟一下，确保WebSocket连接稳定
    setTimeout(() => {
      const state = this.roomStates.get(roomId);
      if (!state || state.isPlaying) return; // 如果已经在播放了，就不要重复操作

      // 修复：避免与enterRoom函数的playNext调用冲突，导致双重音频播放
      // 添加严格的检查条件，避免在正常播放流程中误触发
      if (
        state.queue.length > 0 &&
        state.currentIndex < state.queue.length &&
        !(state as any).howlerPlayer // 确保没有活跃的音频播放器
      ) {
        // 短暂延迟后再检查，因为enterRoom可能刚刚设置了howlerPlayer
        setTimeout(() => {
          const currentState = this.roomStates.get(roomId);
          if (currentState && !(currentState as any).howlerPlayer) {
            // 确保没有进度计时器运行
            // 检查当前索引的项目是否已经播放完成
            const currentItem = currentState.queue[currentState.currentIndex];
            if (currentItem && currentItem.status !== "played") {
              this.playNext(roomId);
            } else {
              // 如果当前项目已播放完成，让正常的播放流程处理
              if (currentState.currentIndex + 1 < currentState.queue.length) {
                // 直接播放下一个，不修改currentIndex
                this.playNext(roomId);
              } else {
                // 已禁用 - 后端不需要请求消息
                // if (
                //   currentState.sessionId &&
                //   currentState.audioQueueWs?.readyState === WebSocket.OPEN
                // )
              }
            }
          }
        }, 200); // 给enterRoom足够的时间设置howlerPlayer
      } else if (state.queue.length === 0) {
        // 已禁用 - 后端不需要请求消息
        // 如果队列为空，主动请求音频
        // if (
        //   state.sessionId &&
        //   state.audioQueueWs?.readyState === WebSocket.OPEN
        // )
      }
    }, 1000); // 增加延迟时间，确保其他流程先完成
  }

  // 初始化插播列表
  newRoomState.insertSegmentList = [];

  // 保存状态到localStorage
  this.saveStateToStorage();
}

export async function restoreConnectionsAfterRefresh(
  this: RootState & { [key: string]: any }
) {
  // 为所有live状态的房间重新建立WebSocket连接和音频播放
  const roomIds = Array.from(this.roomStates.keys());

  // 静默恢复，避免过多弹框
  const originalCurrentRoomId = this.currentRoomId;

  for (const roomId of roomIds) {
    const state = this.roomStates.get(roomId);

    // 🔄 恢复所有有队列的房间，不限于live状态
    const hasQueue = state?.queue && state.queue.length > 0;
    const hasProductList = state && state.productList;
    const isPlayableStatus =
      state?.productList?.status === "live" ||
      state?.productList?.status === "draft";

    if (hasProductList && hasQueue && isPlayableStatus) {
      // 修改：只在真正进入直播间页面时才建立WebSocket连接
      // 检查当前是否在直播间页面
      const isInLiveRoomPage =
        window.location.pathname.includes("/myLiveRoom") &&
        window.location.pathname !== "/myLiveRoom";

      if (isInLiveRoomPage) {
        try {
          // 修复：不再尝试获取sessionId，因为已经废弃了相关接口
          // 如果页面刷新后缺少sessionId，应该由用户手动重新开始直播
          if (!state.sessionId) {
            console.warn(
              `⚠️ 房间${roomId}刷新后缺少sessionId，跳过WebSocket连接恢复`
            );
            continue;
          }

          // 重新建立音频队列WebSocket连接
          await this.initAudioQueueWebSocket(roomId);

          // 修复：只有在开播状态下才自动绑定评论WebSocket
          if (
            state.isBound &&
            state.roomLink &&
            state.productList?.status === "live"
          ) {
            try {
              // 临时设置为当前房间以便绑定WebSocket
              const tempCurrentRoomId = this.currentRoomId;
              this.currentRoomId = roomId;
              await this.bindCommentWebSocket();
              this.sendBindMessage();
              // 立即恢复原来的currentRoomId
              this.currentRoomId = tempCurrentRoomId;
              console.log(`🔌 房间${roomId}已开播，自动绑定评论WebSocket成功`);
            } catch {
              // 静默失败，不显示错误弹框
              console.warn(`🔌 房间${roomId}自动绑定评论WebSocket失败`);
            }
          } else if (state.isBound && state.roomLink) {
            console.log(
              `🔌 房间${roomId}有链接但未开播，跳过自动绑定评论WebSocket`
            );
          }
        } catch {
          // 静默失败，不显示错误弹框
        }
      }

      // 恢复播放状态
      setTimeout(() => {
        // 清除限制，开始播放
        // 旧播放器属性已移除，使用 Howler.js 统一管理
        state.lastRecoveryTime = null;
        if (state.queue && state.queue.length > 0) {
          // 修复索引超出范围的问题
          if (state.currentIndex >= state.queue.length) {
            state.currentIndex = state.queue.length - 1;
          }

          // 寻找第一个未完成的项目
          let resumeIndex = -1;
          for (let i = 0; i < state.queue.length; i++) {
            const item = state.queue[i];
            const playedChars = item.playedCharCount || 0;
            const totalChars = item.text.length;

            if (playedChars < totalChars * 0.95) {
              resumeIndex = i;
              break;
            }
          }

          if (resumeIndex !== -1) {
            // 找到未完成的项目，从这里开始播放
            state.currentIndex = resumeIndex;

            // 强制恢复播放：完全清除保护期和用户交互标记
            state.lastRecoveryTime = null;
            // 旧播放器属性已移除，使用 Howler.js 统一管理

            // 区分前台和后台直播间的恢复策略
            const isBackgroundRoom = originalCurrentRoomId !== roomId;

            // 修复：检查是否为页面刷新恢复，避免与enterRoom冲突
            const isPageRefreshRestore = state.isPageRefreshed;

            if (isPageRefreshRestore) {
              console.log(
                `🔄 页面刷新恢复模式：设置准备状态，等待enterRoom和WebSocket协调播放`
              );
              // 页面刷新时，不立即播放，等待enterRoom和WebSocket的协调
              state.readyToRestore = true;
              return; // 退出，不执行下面的播放逻辑
            }

            // 非页面刷新的正常恢复：延迟检查，避免与enterRoom的playNext冲突
            setTimeout(() => {
              const currentState = this.roomStates.get(roomId);
              if (
                currentState &&
                !(currentState as any).howlerPlayer &&
                !currentState.isPlaying
              ) {
                console.log(
                  `🔄 正常恢复播放: 房间${roomId}, 索引${resumeIndex}`
                );
                this.playNext(roomId)
                  .then(() => {
                    // 恢复播放成功
                  })
                  .catch(() => {
                    // 后台直播间播放失败时，继续下一个音频
                    if (isBackgroundRoom) {
                      setTimeout(() => {
                        if (
                          currentState.currentIndex + 1 <
                          currentState.queue.length
                        ) {
                          this.advanceToNext(roomId);
                        } else {
                          // 已禁用 - 后端不需要请求消息
                        }
                      }, 1000);
                    } else {
                      // 已禁用 - 后端不需要请求消息
                    }
                  });
              } else {
                console.log(
                  `⏸️ 跳过恢复播放：已有播放器或正在播放 (howlerPlayer: ${!!(currentState as any)?.howlerPlayer}, isPlaying: ${currentState?.isPlaying})`
                );
              }
            }, 200);
          } else {
            // 所有项目都已完成，队列播放完毕
            // 已禁用 - 后端不需要请求消息
            // 清除保护期，允许请求新音频
            state.lastRecoveryTime = null;
            state.isPlaying = false;
          }
        } else if (!state.isPlaying && state.productList.status === "live") {
          // 已禁用 - 后端不需要请求消息
          // 只有在完全没有队列时才请求新音频
        }
      }, 1000); // 1秒后恢复播放，确保WebSocket稳定
    }
  }
  // 恢复原来的当前房间ID
  this.currentRoomId = originalCurrentRoomId;
}

// 初始化store，恢复持久化状态
export function initStore(this: RootState & { [key: string]: any }) {
  // 刷新时重置全局交互系统，避免残留状态
  this.globalInteractionSetup = false;
  // 移除未使用的字段：overlayElement

  // 从localStorage恢复状态
  const restored = this.loadStateFromStorage();

  // 刷新时检查当前页面，如果不在直播间则重置currentRoomId
  const currentPath = window.location.pathname;
  const isInLiveRoom =
    currentPath.includes("/myLiveRoom") && currentPath !== "/myLiveRoom";
  if (!isInLiveRoom) {
    this.currentRoomId = null;
  }

  if (restored) {
    // 直接恢复连接
    setTimeout(() => {
      this.restoreConnectionsAfterRefresh();
    }, 1000);
  }

  // 设置页面监听器
  this.setupPageChangeListener();

  // 防止浏览器暂停后台播放的技术
  this.setupBackgroundPlaySupport();

  // 移除：不需要监听页面可见性变化
  // 只要在直播间页面，不管最大化、最小化都应该正常播放
  // 只有在离开直播间页面时才需要处理音频状态

  // 监听页面卸载事件
  window.addEventListener("beforeunload", () => {
    this.saveStateToStorage();
    this.forcemuteAllRooms();
    this.disconnectAllWebSocketsOnRefresh();
  });

  // 监听页面刷新事件（来自全局事件）
  import("@/utils/emitter/mitt")
    .then(({ emitter }) => {
      emitter.on("pageRefresh", () => {
        console.log("🚫 收到页面刷新事件，断开所有WebSocket连接");
        this.disconnectAllWebSocketsOnRefresh();
      });

      // 新增：监听产品删除事件，自动刷新直播间产品列表
      emitter.on("productDeleted", data => {
        console.log(
          `🗑️ 收到产品删除事件: ${data.productName} (ID: ${data.productId})`
        );
        this.handleProductDeleted(data.productId);
      });

      // 兜底：在 initStore 阶段也绑定一次可听监听，避免未调用 initialize 时漏绑
      try {
        if (!(this as any)._audibleListenerBound) {
          (this as any)._audibleListenerBound = true;
          console.log("[audible-listener] bound once");
          emitter.on("apple:howler-audible", (payload: any) => {
            try {
              if (isAppleLike()) return;
              const rid = payload && payload.roomId;
              if (!rid) return;
              console.log(`[audible-listener] fired room=${rid}`);
              const st = this.roomStates.get(rid);
              if (!st) return;
              if ((st as any).pendingSwitchSuccessMessage) {
                // 二次保险：进度归零
                try {
                  st.currentAudioTime = 0;
                  if (st.currentAudioState)
                    st.currentAudioState.currentTime = 0;
                  const hp: any = (st as any).howlerPlayer;
                  const curHowl: any = hp && (hp as any).currentHowl;
                  const node: HTMLAudioElement | undefined =
                    curHowl &&
                    curHowl._sounds &&
                    curHowl._sounds[0] &&
                    curHowl._sounds[0]._node;
                  if (
                    node &&
                    typeof node.currentTime === "number" &&
                    node.currentTime > 0.05
                  )
                    node.currentTime = 0;
                } catch {}

                const { ElMessage } = require("element-plus");
                ElMessage.success("产品切换成功");
                try {
                  const pid = st.switchingProductId || st.selectedProductId;
                  if (pid && st.cardLoadingMap) st.cardLoadingMap[pid] = false;
                } catch {}
                delete (st as any).pendingSwitchSuccessMessage;
              }
            } catch {}
          });
        }
      } catch {}
    })
    .catch(() => {});
}

// 防止浏览器暂停后台播放的技术
export function setupBackgroundPlaySupport(
  this: RootState & { [key: string]: any }
) {
  // 简化的后台保活机制：只使用定时器检查WebSocket状态
  let backgroundInterval: NodeJS.Timeout | null = null;

  const startBackgroundKeepAlive = () => {
    if (backgroundInterval) return;

    // 每隔2秒检查一次WebSocket连接状态
    backgroundInterval = setInterval(() => {
      if (document.hidden) {
        // 页面隐藏时，检查WebSocket连接状态
        const roomIds = Array.from(this.roomStates.keys());
        roomIds.forEach(roomId => {
          const state = this.roomStates.get(roomId);
          if (state && state.productList?.status === "live") {
            // 检查WebSocket连接状态
            if (
              state.audioQueueWs &&
              state.audioQueueWs.readyState !== WebSocket.OPEN
            ) {
              // 避免重复重连，检查是否已经在重连中
              if (state.audioQueueWs.readyState !== WebSocket.CONNECTING) {
                // 清除恢复保护期，避免影响重连后的音频接收
                state.lastRecoveryTime = null;

                this.initAudioQueueWebSocket(roomId).catch(() => {
                  // 静默处理重连失败
                });
              }
            }
          }
        });
      }
    }, 2000); // 降低检查频率，减少性能影响
  };

  const stopBackgroundKeepAlive = () => {
    if (backgroundInterval) {
      clearInterval(backgroundInterval);
      backgroundInterval = null;
    }
  };

  // 启动后台保活
  startBackgroundKeepAlive();

  // 清理函数
  this.cleanupBackgroundSupport = () => {
    stopBackgroundKeepAlive();
  };
}

// 新增：处理产品删除事件
export function handleProductDeleted(
  this: RootState & { [key: string]: any },
  deletedProductId: string
) {
  console.log(`🗑️ 处理产品删除: ${deletedProductId}`);

  // 遍历所有房间，从产品列表中移除已删除的产品
  this.roomStates.forEach((roomState, roomId) => {
    if (roomState.productList && roomState.productList.products) {
      const originalLength = roomState.productList.products.length;

      // 从产品列表中移除已删除的产品
      roomState.productList.products = roomState.productList.products.filter(
        product => product.id !== deletedProductId
      );

      if (originalLength !== roomState.productList.products.length) {
        console.log(
          `📝 房间${roomId}从产品列表中移除了产品 ${deletedProductId}`
        );

        // 更新选中产品ID列表
        roomState.selectedProductIds = roomState.productList.products.map(
          p => p.id
        );

        // 如果当前选中的产品被删除了，选择第一个产品
        if (roomState.selectedProductId === deletedProductId) {
          if (roomState.productList.products.length > 0) {
            roomState.selectedProductId = roomState.productList.products[0].id;
            roomState.productDetail = roomState.productList.products[0];
            console.log(
              `🔄 房间${roomId}切换到新产品: ${roomState.selectedProductId}`
            );
          } else {
            roomState.selectedProductId = "";
            roomState.productDetail = {};
            console.log(`🚨 房间${roomId}没有可用产品了`);
          }
        }
      }
    }
  });

  // 保存状态到本地存储
  this.saveStateToStorage();
}
